import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type { ExpenseType } from '~/schemas/expenseTypes'
import { createExpenseTypeSchema } from '~/schemas/expenseTypes'

export const useExpenseTypesStore = defineStore('expense-types', () => {
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const log = useLogger('store:expenseTypes')
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)
  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $dialog, $confirm } = nuxtApp

  // State
  const expenseTypes: Ref<ExpenseType[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const selectedExpenseType: Ref<ExpenseType | null> = ref(null)
  const parentOptions: Ref<Array<{ value: number, label: string }>> = ref([])

  // Modal states
  const showCreateModal: Ref<boolean> = ref(false)
  const showEditModal: Ref<boolean> = ref(false)

  // Fetch expense types
  async function fetchExpenseTypes(): Promise<void> {
    if (!businessId.value) {
      log.error('Business ID not found')
      return
    }

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/expenses/types`)
      // Use the data array directly (contains all expense types for management)
      expenseTypes.value = res.data || []
      parentOptions.value = res.parentSelect || []
    }
    catch (error) {
      log.error('Failed to fetch expense types:', error)
      useErrorToast(t('Failed to load expense types'))
    }
    finally {
      loading.value = false
    }
  }

  // Create expense type
  async function createExpenseType(formData: Partial<ExpenseType>): Promise<boolean> {
    if (!businessId.value) return false

    const data = {
      ...formData,
      business_id: businessId.value,
    }

    // Validate with Zod schema
    const validatedData = createExpenseTypeSchema.safeParse(data)

    if (!validatedData.success) {
      log.error('Validation failed:', validatedData.error)
      useErrorToast(t('Please check the form data and try again'))
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/expenses/types`, validatedData.data)

      if (res?.success) {
        useSuccessToast(t('Expense type created successfully'))
        await fetchExpenseTypes()
        return true
      }
      return false
    }
    catch (error: any) {
      log.error('Failed to create expense type:', error)
      useErrorToast(error?.data?.message || t('Failed to create expense type'))
      return false
    }
  }

  // Update expense type
  async function updateExpenseType(id: number, formData: Partial<ExpenseType>): Promise<boolean> {
    if (!businessId.value) return false

    try {
      const res = await put(`/api/business/${businessId.value}/expenses/types/${id}`, formData)

      if (res?.success) {
        useSuccessToast(t('Expense type updated successfully'))
        await fetchExpenseTypes()
        return true
      }
      return false
    }
    catch (error: any) {
      log.error('Failed to update expense type:', error)
      useErrorToast(error?.data?.message || t('Failed to update expense type'))
      return false
    }
  }

  // Delete expense type with confirmation
  async function deleteExpenseType(id: number): Promise<void> {
    if (!businessId.value) return

    $confirm.require({
      group: 'inline',
      message: t('Do you want to delete this expense type?'),
      header: t('Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Delete'),
        severity: 'danger',
      },
      accept: async () => {
        try {
          const res = await del(`/api/business/${businessId.value}/expenses/types/${id}`)

          if (res?.success) {
            useSuccessToast(t('Expense type deleted successfully'))
            await fetchExpenseTypes()
          }
        }
        catch (error: any) {
          log.error('Failed to delete expense type:', error)
          const message = error?.data?.statusMessage || error?.data?.message
          if (message?.includes('in use')) {
            useErrorToast(t('Cannot delete expense type that is in use'))
          }
          else {
            useErrorToast(message || t('Failed to delete expense type'))
          }
        }
      },
    })
  }

  // Toggle active status
  async function toggleActive(id: number, active: boolean): Promise<boolean> {
    const expenseType = expenseTypes.value.find(et => et.id === id)
    if (!expenseType) return false

    // Only send the fields that can be updated
    return await updateExpenseType(id, {
      name: expenseType.name,
      description: expenseType.description || null,
      active,
    })
  }

  // Dialog methods
  async function openCreateDialog(): Promise<void> {
    const { default: CreateModal } = await import('~/components/Admin/ExpenseTypes/CreateModal.vue')

    $dialog.open(CreateModal, {
      props: {
        header: t('Add new expense type'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchExpenseTypes()
      },
    })
  }

  async function openEditDialog(expenseType: ExpenseType): Promise<void> {
    const { default: EditModal } = await import('~/components/Admin/ExpenseTypes/EditModal.vue')

    $dialog.open(EditModal, {
      data: {
        expenseType,
      },
      props: {
        header: t('Edit expense type'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchExpenseTypes()
      },
    })
  }

  // Reset store
  function reset(): void {
    expenseTypes.value = []
    loading.value = false
    selectedExpenseType.value = null
    parentOptions.value = []
    showCreateModal.value = false
    showEditModal.value = false
  }

  return {
    // State
    expenseTypes,
    loading,
    selectedExpenseType,
    parentOptions,
    showCreateModal,
    showEditModal,

    // Methods
    fetchExpenseTypes,
    createExpenseType,
    updateExpenseType,
    deleteExpenseType,
    toggleActive,

    // Dialog methods
    openCreateDialog,
    openEditDialog,

    // Reset
    reset,
  }
},
{
  persist: {
    key: 'expense-types',
    storage: localStorage,
    paths: [], // Don't persist anything by default
  },
})
